import { CheckIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import FilterSelect from '@/components/ui/filter-select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/contexts/ToastContext';
import { useEnhancedApi } from '@/hooks/use-enhanced-api';
import { useInventory } from '@/hooks/use-inventory';
import type {
  InventoryFormData,
  InventoryFormProps,
  StepIndicatorProps,
} from '@/types/module';
import { MEASUREMENT_UNIT_OPTIONS, PRODUCT_TYPE_OPTIONS } from '@/types/module';

// Step indicator component
const StepIndicator: React.FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps,
  stepLabels,
}) => {
  return (
    <div className='flex items-center justify-between mb-8'>
      {Array.from({ length: totalSteps }, (_, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        const isLast = stepNumber === totalSteps;

        return (
          <React.Fragment key={stepNumber}>
            <div className='flex flex-col items-center'>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isCompleted
                    ? 'bg-success-60 text-white'
                    : isActive
                      ? 'bg-success-60 text-white'
                      : 'bg-gray-20 text-gray-60'
                }`}
              >
                {isCompleted ? <CheckIcon className='w-4 h-4' /> : stepNumber}
              </div>
              <span className='text-xs mt-2 text-gray-60 text-center'>
                {stepLabels[index]}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mx-4 ${
                  isCompleted || (isActive && stepNumber > 1)
                    ? 'bg-success-60'
                    : 'bg-gray-20'
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const InventoryForm: React.FC<InventoryFormProps> = ({
  mode,
  product,
  onSuccess,
  onCancel,
}) => {
  const api = useEnhancedApi();
  const { showSuccess, showError } = useToast();
  const { categories, fetchCategories } = useInventory();

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isVariant, setIsVariant] = useState(product?.isVariant || false);
  const [variants, setVariants] = useState(
    product?.variant?.map((v: any) => ({
      name: v.name,
      quantity: v.quantity,
      price: v?.pricing?.sellingPrice ?? v.price ?? 0,
      reorderLevel: v.reorderLevel,
      costPrice: v?.pricing?.costPrice ?? undefined,
      wholesalePrice: v?.pricing?.wholesalePrice ?? undefined,
    })) || [
      {
        name: '',
        quantity: 0,
        price: 0,
        reorderLevel: 0,
        costPrice: undefined,
        wholesalePrice: undefined,
      },
    ]
  );

  const totalSteps = 2;
  const stepLabels = ['Product Details', 'Variants / Pricing'];

  // Options aligned with backend enums
  const PRODUCT_CLASS_OPTIONS = [
    { value: 'raw_material', label: 'Raw Material' },
    { value: 'finished_good', label: 'Finished Good' },
    { value: 'semi_finished', label: 'Semi-finished' },
    { value: 'consumable', label: 'Consumable' },
    { value: 'service', label: 'Service' },
  ];
  const STORAGE_CONDITION_OPTIONS = [
    { value: 'ambient', label: 'Ambient' },
    { value: 'refrigerated', label: 'Refrigerated' },
    { value: 'frozen', label: 'Frozen' },
    { value: 'controlled_atmosphere', label: 'Controlled Atmosphere' },
    { value: 'dry_storage', label: 'Dry Storage' },
    { value: 'cold_chain_required', label: 'Cold Chain Required' },
  ];

  const { control, handleSubmit, watch, trigger, reset, setValue } =
    useForm<InventoryFormData>({
      defaultValues:
        mode === 'create'
          ? {
              name: '',
              description: '',
              shortDescription: '',
              brand: '',
              // identification
              sku: '',
              barcode: '',
              internalCode: '',
              // category
              category: '',
              categorySecondary: '',
              categorySubcategory: '',
              // classification
              productClass: 'raw_material',
              // measurement
              measurementUnit: 'kg',
              measurementUnitSecondary: '',
              conversionFactor: null,
              // stock/pricing
              quantity: null,
              costPrice: null,
              sellingPrice: null,
              wholesalePrice: null,
              retailPrice: null,
              currency: 'NGN',
              reorderLevel: null,
              // product type
              isVariant: false,
              variants: [],
              // quality
              qualityGrade: 'A',
              // storage
              storageWarehouse: '',
              storageConditions: 'ambient',
              storageTemperatureMin: null,
              storageTemperatureMax: null,
              storageTemperatureOptimal: null,
              storageHumidityMin: null,
              storageHumidityMax: null,
              storageHumidityOptimal: null,
              storageShelfLifeDays: null,
              storageShelfLifeConditions: '',
              storageLocationZone: '',
              storageLocationAisle: '',
              storageLocationShelf: '',
              storageLocationBin: '',
              // auto reorder
              autoReorderEnabled: false,
              autoReorderPoint: null,
              autoReorderQty: null,
              leadTimeDays: 7,
              // compliance
              requiresCertification: false,
              hazardousFlag: false,
              restrictedSale: false,
              exportEligible: true,
            }
          : {
              name: product?.name || '',
              description: product?.description || '',
              shortDescription: (product as any)?.shortDescription || '',
              brand: (product as any)?.brand || '',
              // identification
              sku: (product as any)?.sku || '',
              barcode: (product as any)?.barcode || '',
              internalCode: (product as any)?.internalCode || '',
              // category
              category:
                typeof product?.category === 'string'
                  ? (product?.category as unknown as string)
                  : (product?.category as any)?.primary || 'value-pick',
              categorySecondary: (product as any)?.category?.secondary || '',
              categorySubcategory:
                (product as any)?.category?.subcategory || '',
              // classification
              productClass: (product as any)?.productClass || 'raw_material',
              // measurement
              measurementUnit:
                typeof product?.measurementUnit === 'string'
                  ? (product?.measurementUnit as unknown as string)
                  : (product as any)?.measurementUnit?.primary || 'kg',
              measurementUnitSecondary:
                (product as any)?.measurementUnit?.secondary || '',
              conversionFactor:
                (product as any)?.measurementUnit?.conversionFactor || null,
              // stock/pricing
              quantity: product?.quantity ?? 0,
              costPrice: (product as any)?.pricing?.costPrice ?? null,
              sellingPrice: (product as any)?.pricing?.sellingPrice ?? null,
              wholesalePrice: (product as any)?.pricing?.wholesalePrice ?? null,
              retailPrice: (product as any)?.pricing?.retailPrice ?? null,
              currency: (product as any)?.pricing?.currency || 'NGN',
              reorderLevel: product?.reorderLevel ?? 0,
              // product type
              isVariant: product?.isVariant || false,
              variants:
                (product?.variant as any)?.map((v: any) => ({
                  name: v.name,
                  quantity: v.quantity,
                  price: v?.pricing?.sellingPrice ?? v.price ?? 0,
                  reorderLevel: v.reorderLevel ?? 0,
                })) || [],
              // quality
              qualityGrade: (product as any)?.quality?.grade || 'A',
              // storage
              storageWarehouse:
                (product as any)?.storage?.location?.warehouse || '',
              storageConditions:
                (product as any)?.storage?.conditions || 'ambient',
              storageTemperatureMin:
                (product as any)?.storage?.temperature?.min ?? null,
              storageTemperatureMax:
                (product as any)?.storage?.temperature?.max ?? null,
              storageTemperatureOptimal:
                (product as any)?.storage?.temperature?.optimal ?? null,
              storageHumidityMin:
                (product as any)?.storage?.humidity?.min ?? null,
              storageHumidityMax:
                (product as any)?.storage?.humidity?.max ?? null,
              storageHumidityOptimal:
                (product as any)?.storage?.humidity?.optimal ?? null,
              storageShelfLifeDays:
                (product as any)?.storage?.shelfLife?.days ?? null,
              storageShelfLifeConditions:
                (product as any)?.storage?.shelfLife?.conditions || '',
              storageLocationZone:
                (product as any)?.storage?.location?.zone || '',
              storageLocationAisle:
                (product as any)?.storage?.location?.aisle || '',
              storageLocationShelf:
                (product as any)?.storage?.location?.shelf || '',
              storageLocationBin:
                (product as any)?.storage?.location?.bin || '',
              // auto reorder
              autoReorderEnabled: Boolean(
                (product as any)?.autoReorder?.enabled
              ),
              autoReorderPoint:
                (product as any)?.autoReorder?.reorderPoint ?? null,
              autoReorderQty:
                (product as any)?.autoReorder?.reorderQuantity ?? null,
              leadTimeDays: (product as any)?.autoReorder?.leadTimeDays ?? 7,
              // compliance
              requiresCertification:
                Boolean((product as any)?.compliance?.requiresCertification) ||
                false,
              hazardousFlag:
                Boolean((product as any)?.compliance?.hazardousFlag) || false,
              restrictedSale:
                Boolean((product as any)?.compliance?.restrictedSale) || false,
              exportEligible:
                (product as any)?.compliance?.exportEligible ?? true,
            },
    });

  const watchedIsVariant = watch('isVariant');

  useEffect(() => {
    setIsVariant(watchedIsVariant);
    if (watchedIsVariant) {
      setValue('quantity', 0); // Set to 0 for variant products
    }
  }, [watchedIsVariant, setValue]);

  // Ensure categories are loaded
  useEffect(() => {
    if (!categories || categories.length === 0) {
      fetchCategories();
    }
  }, [categories, fetchCategories]);

  // Form validation for each step
  const validateStep = useCallback(
    async (step: number): Promise<boolean> => {
      if (step === 1) {
        // Product Details step
        return await trigger(['name', 'category', 'measurementUnit']);
      } else if (step === 2) {
        // Variants step - validate based on product type
        if (isVariant) {
          // For variant products, validate that we have at least one valid variant
          const validVariants = variants.filter(v => v.name.trim() !== '');
          if (validVariants.length === 0) {
            showError('Please add at least one variant');
            return false;
          }
          return true;
        } else {
          // For simple products, validate pricing and quantity
          return await trigger([
            'costPrice',
            'sellingPrice',
            'quantity',
            'reorderLevel',
          ]);
        }
      }
      return true;
    },
    [trigger, isVariant, variants, showError]
  );

  // Handle step navigation
  const handleNextStep = useCallback(async () => {
    const isValid = await validateStep(currentStep);

    if (isValid) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Final step - submit form
        await handleSubmit(onSubmit)();
      }
    }
  }, [currentStep, totalSteps, validateStep, handleSubmit]);

  const handlePrevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleCancel = useCallback(() => {
    reset();
    setCurrentStep(1);
    setIsVariant(false);
    setVariants([
      {
        name: '',
        quantity: 0,
        price: 0,
        reorderLevel: 0,
        costPrice: undefined,
        wholesalePrice: undefined,
      },
    ]);
    onCancel();
  }, [reset, onCancel]);

  // Expose handlers for dynamic button system
  useEffect(() => {
    const handlers = {
      handleNextStep,
      handlePrevStep,
      handleCancel,
      isLoading,
      currentStep,
      totalSteps,
    };

    // Store handlers globally based on mode
    if (mode === 'create') {
      (window as any).inventoryCreateHandlers = handlers;
    } else {
      (window as any).inventoryUpdateHandlers = handlers;
    }

    // Cleanup on unmount
    return () => {
      if (mode === 'create' && (window as any).inventoryCreateHandlers) {
        delete (window as any).inventoryCreateHandlers;
      } else if (mode === 'update' && (window as any).inventoryUpdateHandlers) {
        delete (window as any).inventoryUpdateHandlers;
      }
    };
  }, [
    mode,
    handleNextStep,
    handlePrevStep,
    handleCancel,
    isLoading,
    currentStep,
    totalSteps,
  ]);

  const onSubmit = async (formData: InventoryFormData) => {
    setIsLoading(true);

    // Helper to generate variantId compatible with backend expectations
    const generateVariantId = () => {
      const w = window as any;
      if (w?.crypto?.randomUUID) return w.crypto.randomUUID();
      return `var_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
    };

    try {
      // Build payload per backend schema
      const submitData: any = {
        name: formData.name,
        description: formData.description,
        shortDescription: formData.shortDescription,
        brand: formData.brand,
        sku: formData.sku || undefined,
        barcode: formData.barcode || undefined,
        internalCode: formData.internalCode || undefined,
        category: {
          primary: formData.category,
          secondary: formData.categorySecondary || undefined,
          subcategory: formData.categorySubcategory || undefined,
        },
        productClass: formData.productClass || undefined,
        measurementUnit: {
          primary: formData.measurementUnit,
          secondary: formData.measurementUnitSecondary || undefined,
          conversionFactor:
            formData.conversionFactor !== null
              ? formData.conversionFactor || undefined
              : undefined,
        },
        isVariant: isVariant,
      };

      if (isVariant) {
        // For variant products, include variants, compute summary fields, and ensure variantId
        const prepared = variants
          .map((v, __idx) => ({ ...v, __idx }))
          .filter(v => v.name.trim() !== '');

        const totalQuantity = prepared.reduce(
          (s, v) => s + (Number(v.quantity) || 0),
          0
        );
        const prices = prepared
          .map(v => Number(v.price) || 0)
          .filter(n => !isNaN(n));
        const avgPrice =
          prices.length > 0
            ? prices.reduce((s, n) => s + n, 0) / prices.length
            : 0;

        submitData.quantity = totalQuantity;
        submitData.pricing = {
          costPrice: Number((avgPrice * 0.7).toFixed(2)),
          sellingPrice: Number(avgPrice.toFixed(2)),
          currency: formData.currency || 'NGN',
        };

        submitData.variant = prepared.map(v => ({
          variantId:
            (product as any)?.variant?.[v.__idx]?.variantId ||
            generateVariantId(),
          name: v.name,
          quantity: v.quantity,
          pricing: (() => {
            const p: any = { sellingPrice: v.price };
            if (typeof (v as any).costPrice === 'number')
              p.costPrice = (v as any).costPrice;
            if (typeof (v as any).wholesalePrice === 'number')
              p.wholesalePrice = (v as any).wholesalePrice;
            return p;
          })(),
          reorderLevel: v.reorderLevel,
        }));
      } else {
        // For non-variant products, include pricing, quantity, and reorderLevel
        submitData.quantity = formData.quantity ?? 0;
        submitData.reorderLevel = formData.reorderLevel ?? 0;
        submitData.pricing = {
          costPrice: formData.costPrice ?? 0,
          sellingPrice: formData.sellingPrice ?? 0,
          wholesalePrice: formData.wholesalePrice || undefined,
          retailPrice: formData.retailPrice || undefined,
          currency: formData.currency || 'NGN',
        };
      }

      // Advanced optional fields
      if (formData.qualityGrade) {
        submitData.quality = { grade: formData.qualityGrade };
      }

      // Storage mapping (conditions, environment, shelf life, location)
      const storage: any = {};
      if (formData.storageConditions)
        storage.conditions = formData.storageConditions;
      if (
        formData.storageTemperatureMin !== null ||
        formData.storageTemperatureMax !== null ||
        formData.storageTemperatureOptimal !== null
      ) {
        storage.temperature = {
          min: formData.storageTemperatureMin ?? undefined,
          max: formData.storageTemperatureMax ?? undefined,
          optimal: formData.storageTemperatureOptimal ?? undefined,
        };
      }
      if (
        formData.storageHumidityMin !== null ||
        formData.storageHumidityMax !== null ||
        formData.storageHumidityOptimal !== null
      ) {
        storage.humidity = {
          min: formData.storageHumidityMin ?? undefined,
          max: formData.storageHumidityMax ?? undefined,
          optimal: formData.storageHumidityOptimal ?? undefined,
        };
      }
      if (
        formData.storageShelfLifeDays !== null ||
        (formData.storageShelfLifeConditions &&
          formData.storageShelfLifeConditions.trim() !== '')
      ) {
        storage.shelfLife = {
          days: formData.storageShelfLifeDays ?? undefined,
          conditions: formData.storageShelfLifeConditions || undefined,
        };
      }
      if (
        formData.storageWarehouse ||
        formData.storageLocationZone ||
        formData.storageLocationAisle ||
        formData.storageLocationShelf ||
        formData.storageLocationBin
      ) {
        storage.location = {
          warehouse: formData.storageWarehouse || undefined,
          zone: formData.storageLocationZone || undefined,
          aisle: formData.storageLocationAisle || undefined,
          shelf: formData.storageLocationShelf || undefined,
          bin: formData.storageLocationBin || undefined,
        };
      }
      if (Object.keys(storage).length > 0) {
        submitData.storage = storage;
      }

      submitData.autoReorder = {
        enabled: Boolean(formData.autoReorderEnabled),
        reorderPoint: formData.autoReorderPoint || undefined,
        reorderQuantity: formData.autoReorderQty || undefined,
        leadTimeDays: formData.leadTimeDays || undefined,
      };

      // Compliance flags
      submitData.compliance = {
        requiresCertification: Boolean(formData.requiresCertification),
        hazardousFlag: Boolean(formData.hazardousFlag),
        restrictedSale: Boolean(formData.restrictedSale),
        exportEligible:
          formData.exportEligible !== undefined
            ? Boolean(formData.exportEligible)
            : undefined,
      };

      const response =
        mode === 'create'
          ? await api.post('/inventories', submitData)
          : await api.put(`/inventories/${product?._id}`, submitData);

      if (response.success) {
        showSuccess(
          response.message ||
            `Product ${mode === 'create' ? 'created' : 'updated'} successfully`
        );
        reset();
        setCurrentStep(1);
        await onSuccess();
      } else {
        showError(
          response.error?.message ||
            `Failed to ${mode === 'create' ? 'create' : 'update'} product`
        );
      }
    } catch (error) {
      showError(
        `An error occurred while ${mode === 'create' ? 'creating' : 'updating'} the product`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Variant management functions
  const addVariant = () => {
    setVariants([
      ...variants,
      {
        name: '',
        quantity: 0,
        price: 0,
        reorderLevel: 0,
        costPrice: undefined,
        wholesalePrice: undefined,
      },
    ]);
  };

  const removeVariant = (index: number) => {
    if (variants.length > 1) {
      setVariants(variants.filter((_, i) => i !== index));
    }
  };

  const updateVariant = (
    index: number,
    field: keyof (typeof variants)[0],
    value: string | number
  ) => {
    const updatedVariants = variants.map((variant, i) =>
      i === index ? { ...variant, [field]: value } : variant
    );
    setVariants(updatedVariants);
  };

  // Computed summary for variant products
  const variantSummary = useMemo(() => {
    if (!isVariant) return null;
    const cleaned = variants.filter(v => v.name.trim() !== '');
    const totalQuantity = cleaned.reduce(
      (s, v) => s + (Number(v.quantity) || 0),
      0
    );
    const prices = cleaned
      .map(v => Number(v.price) || 0)
      .filter(n => !isNaN(n));
    const avgPrice =
      prices.length > 0 ? prices.reduce((s, n) => s + n, 0) / prices.length : 0;
    const topPricing = {
      costPrice: Number((avgPrice * 0.7).toFixed(2)),
      sellingPrice: Number(avgPrice.toFixed(2)),
      currency: (watch('currency') as any) || 'NGN',
    };
    return { totalQuantity, topPricing };
  }, [isVariant, variants, watch]);

  // Debug payload preview toggle
  const [showDebug, setShowDebug] = useState(false);

  const previewPayload = useMemo(() => {
    const formVals = watch();
    const base: any = {
      name: formVals.name,
      description: formVals.description,
      shortDescription: formVals.shortDescription,
      brand: formVals.brand,
      category: {
        primary: formVals.category,
        secondary: formVals.categorySecondary || undefined,
        subcategory: formVals.categorySubcategory || undefined,
      },
      measurementUnit: {
        primary: formVals.measurementUnit,
        secondary: formVals.measurementUnitSecondary || undefined,
        conversionFactor:
          formVals.conversionFactor !== null
            ? formVals.conversionFactor || undefined
            : undefined,
      },
      isVariant: isVariant,
    };

    if (isVariant) {
      const cleaned = variants.filter(v => v.name.trim() !== '');
      const totalQuantity = cleaned.reduce(
        (s, v) => s + (Number(v.quantity) || 0),
        0
      );
      const prices = cleaned
        .map(v => Number(v.price) || 0)
        .filter((n: number) => !isNaN(n));
      const avgPrice =
        prices.length > 0
          ? prices.reduce((s, n) => s + n, 0) / prices.length
          : 0;
      base.quantity = totalQuantity;
      base.pricing = {
        costPrice: Number((avgPrice * 0.7).toFixed(2)),
        sellingPrice: Number(avgPrice.toFixed(2)),
        currency: formVals.currency || 'NGN',
      };
      base.variant = cleaned.map((v, idx) => {
        const pricing: any = { sellingPrice: v.price };
        if (typeof v.costPrice === 'number') pricing.costPrice = v.costPrice;
        if (typeof v.wholesalePrice === 'number')
          pricing.wholesalePrice = v.wholesalePrice;
        return {
          variantId:
            (product as any)?.variant?.[idx]?.variantId || 'will_be_generated',
          name: v.name,
          quantity: v.quantity,
          pricing,
          reorderLevel: v.reorderLevel,
        };
      });
    } else {
      base.quantity = formVals.quantity ?? 0;
      base.reorderLevel = formVals.reorderLevel ?? 0;
      base.pricing = {
        costPrice: formVals.costPrice ?? 0,
        sellingPrice: formVals.sellingPrice ?? 0,
        wholesalePrice: formVals.wholesalePrice || undefined,
        retailPrice: formVals.retailPrice || undefined,
        currency: formVals.currency || 'NGN',
      };
    }

    if (formVals.qualityGrade) base.quality = { grade: formVals.qualityGrade };
    if (formVals.storageWarehouse)
      base.storage = { location: { warehouse: formVals.storageWarehouse } };
    base.autoReorder = {
      enabled: Boolean(formVals.autoReorderEnabled),
      reorderPoint: formVals.autoReorderPoint || undefined,
      reorderQuantity: formVals.autoReorderQty || undefined,
      leadTimeDays: formVals.leadTimeDays || undefined,
    };
    return base;
  }, [watch, isVariant, variants, product]);

  // Render step content
  const renderStepContent = () => {
    if (currentStep === 1) {
      return (
        <div className='space-y-6'>
          {/* Current Product Info for Update Mode */}
          {mode === 'update' && product && (
            <div className='bg-gray-5 p-4 rounded-lg'>
              <h4 className='text-sm font-medium text-gray-90 mb-3'>
                Current Product Information
              </h4>
              <div className='space-y-2 text-sm'>
                <div className='flex justify-between'>
                  <span>Category:</span>
                  <span className='font-semibold'>{product.category}</span>
                </div>
                <div className='flex justify-between'>
                  <span>Type:</span>
                  <span className='font-semibold'>
                    {product.isVariant
                      ? 'Product with Variants'
                      : 'Simple Product'}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Quantity:</span>
                  <span className='font-semibold text-success-60'>
                    {product.quantity} {product.measurementUnit}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Product Name */}
          <Controller
            name='name'
            control={control}
            rules={{ required: 'Product name is required' }}
            render={({ field, fieldState }) => (
              <Input
                label='Product Name'
                {...field}
                placeholder='Enter product name'
                error={fieldState?.error?.message}
              />
            )}
          />

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='description'>Description (Optional)</Label>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  placeholder='Enter product description'
                  rows={3}
                />
              )}
            />
          </div>

          {/* Short Description */}
          <Controller
            name='shortDescription'
            control={control}
            render={({ field }) => (
              <Input
                label='Short Description'
                {...field}
                placeholder='Concise summary (max 200 chars)'
                maxLength={200}
              />
            )}
          />

          {/* Brand (Optional) */}
          <Controller
            name='brand'
            control={control}
            render={({ field }) => (
              <Input
                label='Brand (Optional)'
                {...field}
                placeholder='e.g., Acme'
              />
            )}
          />

          {/* Identification (Optional) */}
          <div className='grid grid-cols-3 gap-4'>
            <Controller
              name='sku'
              control={control}
              render={({ field }) => (
                <Input
                  label='SKU (Optional)'
                  {...field}
                  placeholder='Auto-generated if empty'
                />
              )}
            />
            <Controller
              name='barcode'
              control={control}
              render={({ field }) => (
                <Input
                  label='Barcode (Optional)'
                  {...field}
                  placeholder='e.g., EAN/UPC'
                />
              )}
            />
            <Controller
              name='internalCode'
              control={control}
              render={({ field }) => (
                <Input
                  label='Internal Code (Optional)'
                  {...field}
                  placeholder='Internal reference'
                />
              )}
            />
          </div>

          {/* Category */}
          <Controller
            name='category'
            control={control}
            rules={{ required: 'Category is required' }}
            render={({ field, fieldState }) => (
              <FilterSelect
                label='Category'
                name='category'
                value={field.value}
                options={(categories || []).map(c => ({
                  value: c,
                  label: c
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, ch => ch.toUpperCase()),
                }))}
                placeholder='Select category (fetched)'
                error={fieldState?.error?.message}
                onChange={field.onChange}
                className='w-full'
              />
            )}
          />

          {/* Secondary Category (Optional) */}
          <Controller
            name='categorySecondary'
            control={control}
            render={({ field }) => (
              <Input
                label='Secondary Category (Optional)'
                {...field}
                placeholder='e.g., Leafy greens'
              />
            )}
          />

          {/* Subcategory (Optional) */}
          <Controller
            name='categorySubcategory'
            control={control}
            render={({ field }) => (
              <Input
                label='Subcategory (Optional)'
                {...field}
                placeholder='e.g., Organic'
              />
            )}
          />

          {/* Product Class */}
          <Controller
            name='productClass'
            control={control}
            render={({ field }) => (
              <FilterSelect
                label='Product Class'
                name='productClass'
                value={field.value || 'raw_material'}
                options={PRODUCT_CLASS_OPTIONS}
                placeholder='Select product class'
                onChange={field.onChange}
                className='w-full'
              />
            )}
          />

          {/* Measurement Unit */}
          <Controller
            name='measurementUnit'
            control={control}
            rules={{ required: 'Measurement unit is required' }}
            render={({ field, fieldState }) => (
              <FilterSelect
                label='Measurement Unit'
                name='measurementUnit'
                value={field.value}
                options={MEASUREMENT_UNIT_OPTIONS}
                placeholder='Select measurement unit'
                error={fieldState?.error?.message}
                onChange={field.onChange}
                className='w-full'
              />
            )}
          />

          {/* Secondary Unit (Optional) */}
          <Controller
            name='measurementUnitSecondary'
            control={control}
            render={({ field }) => (
              <Input
                label='Secondary Unit (Optional)'
                {...field}
                placeholder='e.g., g'
              />
            )}
          />

          {/* Conversion Factor (Optional) */}
          <Controller
            name='conversionFactor'
            control={control}
            render={({ field }) => (
              <Input
                label='Conversion Factor (Optional)'
                type='number'
                value={field.value ?? ''}
                placeholder='e.g., 1000 (g to kg)'
                onChange={e => field.onChange(Number(e.target.value))}
              />
            )}
          />

          {/* Product Type */}
          <Controller
            name='isVariant'
            control={control}
            render={({ field }) => (
              <FilterSelect
                label='Product Type'
                name='isVariant'
                value={field.value ? 'true' : 'false'}
                options={PRODUCT_TYPE_OPTIONS}
                placeholder='Select product type'
                onChange={value => field.onChange(value === 'true')}
                className='w-full'
              />
            )}
          />
        </div>
      );
    }

    if (currentStep === 2) {
      return (
        <div className='space-y-6'>
          {!isVariant ? (
            // Simple Product Fields
            <div className='space-y-6'>
              <div className='grid grid-cols-2 gap-4'>
                <Controller
                  name='quantity'
                  control={control}
                  rules={{
                    required: 'Quantity is required',
                    min: {
                      value: 0,
                      message: 'Quantity must be 0 or greater',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      label='Initial Quantity'
                      {...field}
                      type='number'
                      value={field.value ?? 0}
                      placeholder='Enter quantity'
                      error={fieldState?.error?.message}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />

                <Controller
                  name='costPrice'
                  control={control}
                  rules={{
                    required: 'Cost price is required',
                    min: {
                      value: 0,
                      message: 'Cost price must be 0 or greater',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      label='Cost Price'
                      {...field}
                      type='number'
                      step='0.01'
                      value={field.value ?? 0}
                      placeholder='Enter cost price'
                      error={fieldState?.error?.message}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />

                <Controller
                  name='sellingPrice'
                  control={control}
                  rules={{
                    required: 'Selling price is required',
                    min: {
                      value: 0.01,
                      message: 'Selling price must be greater than 0',
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Input
                      label='Selling Price'
                      {...field}
                      type='number'
                      step='0.01'
                      value={field.value ?? 0}
                      placeholder='Enter selling price'
                      error={fieldState?.error?.message}
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <Controller
                  name='wholesalePrice'
                  control={control}
                  render={({ field }) => (
                    <Input
                      label='Wholesale Price (Optional)'
                      {...field}
                      type='number'
                      step='0.01'
                      value={field.value ?? ''}
                      placeholder='Enter wholesale price'
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />

                <Controller
                  name='retailPrice'
                  control={control}
                  render={({ field }) => (
                    <Input
                      label='Retail Price (Optional)'
                      {...field}
                      type='number'
                      step='0.01'
                      value={field.value ?? ''}
                      placeholder='Enter retail price'
                      onChange={e => field.onChange(Number(e.target.value))}
                    />
                  )}
                />
              </div>

              <Controller
                name='reorderLevel'
                control={control}
                rules={{
                  required: 'Reorder level is required',
                  min: {
                    value: 0,
                    message: 'Reorder level must be 0 or greater',
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    label='Reorder Level'
                    {...field}
                    type='number'
                    value={field.value ?? 0}
                    placeholder='Enter reorder level'
                    error={fieldState?.error?.message}
                    onChange={e => field.onChange(Number(e.target.value))}
                  />
                )}
              />

              {/* Advanced Settings */}
              <div className='space-y-4 border rounded-lg p-4'>
                <h4 className='text-sm font-medium text-gray-90'>
                  Advanced Settings
                </h4>

                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='currency'
                    control={control}
                    render={({ field }) => (
                      <FilterSelect
                        label='Currency'
                        name='currency'
                        value={field.value || 'NGN'}
                        options={[
                          { value: 'NGN', label: 'NGN' },
                          { value: 'USD', label: 'USD' },
                          { value: 'EUR', label: 'EUR' },
                          { value: 'GBP', label: 'GBP' },
                        ]}
                        onChange={field.onChange}
                        className='w-full'
                      />
                    )}
                  />

                  <Controller
                    name='qualityGrade'
                    control={control}
                    render={({ field }) => (
                      <FilterSelect
                        label='Quality Grade'
                        name='qualityGrade'
                        value={field.value || 'A'}
                        options={[
                          { value: 'Premium', label: 'Premium' },
                          { value: 'A', label: 'Grade A' },
                          { value: 'B', label: 'Grade B' },
                          { value: 'C', label: 'Grade C' },
                          { value: 'Rejected', label: 'Rejected' },
                        ]}
                        onChange={field.onChange}
                        className='w-full'
                      />
                    )}
                  />
                </div>

                {/* Storage & Environment */}
                <div className='space-y-3'>
                  <Label>Storage & Environment</Label>
                  <div className='grid grid-cols-2 gap-4'>
                    <Controller
                      name='storageConditions'
                      control={control}
                      render={({ field }) => (
                        <FilterSelect
                          label='Conditions'
                          name='storageConditions'
                          value={field.value || 'ambient'}
                          options={STORAGE_CONDITION_OPTIONS}
                          onChange={field.onChange}
                          className='w-full'
                        />
                      )}
                    />
                    <Controller
                      name='storageWarehouse'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Warehouse (Optional)'
                          {...field}
                          placeholder='e.g., Main Warehouse'
                        />
                      )}
                    />
                  </div>

                  <div className='grid grid-cols-3 gap-4'>
                    <Controller
                      name='storageTemperatureMin'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Temp Min (°C)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    <Controller
                      name='storageTemperatureMax'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Temp Max (°C)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    <Controller
                      name='storageTemperatureOptimal'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Temp Optimal (°C)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                  </div>

                  <div className='grid grid-cols-3 gap-4'>
                    <Controller
                      name='storageHumidityMin'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Humidity Min (%)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    <Controller
                      name='storageHumidityMax'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Humidity Max (%)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    <Controller
                      name='storageHumidityOptimal'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Humidity Optimal (%)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <Controller
                      name='storageShelfLifeDays'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Shelf Life (days)'
                          type='number'
                          value={field.value ?? ''}
                          onChange={e => field.onChange(Number(e.target.value))}
                        />
                      )}
                    />
                    <Controller
                      name='storageShelfLifeConditions'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Shelf Life Conditions (Optional)'
                          {...field}
                          placeholder='e.g., Keep sealed'
                        />
                      )}
                    />
                  </div>

                  <div className='grid grid-cols-4 gap-4'>
                    <Controller
                      name='storageLocationZone'
                      control={control}
                      render={({ field }) => (
                        <Input label='Zone' {...field} placeholder='e.g., Z1' />
                      )}
                    />
                    <Controller
                      name='storageLocationAisle'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Aisle'
                          {...field}
                          placeholder='e.g., A5'
                        />
                      )}
                    />
                    <Controller
                      name='storageLocationShelf'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Shelf'
                          {...field}
                          placeholder='e.g., S3'
                        />
                      )}
                    />
                    <Controller
                      name='storageLocationBin'
                      control={control}
                      render={({ field }) => (
                        <Input label='Bin' {...field} placeholder='e.g., B12' />
                      )}
                    />
                  </div>
                </div>

                {/* Compliance */}
                <div className='space-y-3'>
                  <Label>Compliance</Label>
                  <div className='grid grid-cols-2 gap-4'>
                    <Controller
                      name='requiresCertification'
                      control={control}
                      render={({ field }) => (
                        <div className='flex items-center justify-between border rounded-md px-3 py-2'>
                          <span className='text-sm'>
                            Requires Certification
                          </span>
                          <input
                            type='checkbox'
                            checked={Boolean(field.value)}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name='hazardousFlag'
                      control={control}
                      render={({ field }) => (
                        <div className='flex items-center justify-between border rounded-md px-3 py-2'>
                          <span className='text-sm'>Hazardous</span>
                          <input
                            type='checkbox'
                            checked={Boolean(field.value)}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name='restrictedSale'
                      control={control}
                      render={({ field }) => (
                        <div className='flex items-center justify-between border rounded-md px-3 py-2'>
                          <span className='text-sm'>Restricted Sale</span>
                          <input
                            type='checkbox'
                            checked={Boolean(field.value)}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        </div>
                      )}
                    />
                    <Controller
                      name='exportEligible'
                      control={control}
                      render={({ field }) => (
                        <div className='flex items-center justify-between border rounded-md px-3 py-2'>
                          <span className='text-sm'>Export Eligible</span>
                          <input
                            type='checkbox'
                            checked={Boolean(field.value ?? true)}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
                <Controller
                  name='storageWarehouse'
                  control={control}
                  render={({ field }) => (
                    <Input
                      label='Warehouse (Optional)'
                      {...field}
                      placeholder='e.g., Main Warehouse'
                    />
                  )}
                />

                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='autoReorderEnabled'
                    control={control}
                    render={({ field }) => (
                      <div className='flex items-center justify-between'>
                        <Label>Enable Auto Reorder</Label>
                        <input
                          type='checkbox'
                          checked={Boolean(field.value)}
                          onChange={e => field.onChange(e.target.checked)}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name='leadTimeDays'
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='Lead Time (days)'
                        type='number'
                        value={field.value ?? 7}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='autoReorderPoint'
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='Reorder Point (Optional)'
                        type='number'
                        value={field.value ?? ''}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                  <Controller
                    name='autoReorderQty'
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='Reorder Quantity (Optional)'
                        type='number'
                        value={field.value ?? ''}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Payload Preview (toggle) */}
              <div className='border rounded-lg p-3 bg-gray-5'>
                <div className='flex items-center justify-between'>
                  <div className='text-sm font-medium text-gray-80'>
                    Payload Preview
                  </div>
                  <button
                    type='button'
                    className='text-sm text-brand-60 hover:text-brand-70'
                    onClick={() => setShowDebug(prev => !prev)}
                  >
                    {showDebug ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showDebug && (
                  <pre className='mt-2 text-xs overflow-auto max-h-64 p-2 bg-white rounded border'>
                    {JSON.stringify(previewPayload, null, 2)}
                  </pre>
                )}
              </div>
            </div>
          ) : (
            // Variant Products
            <div className='space-y-6'>
              <div className='flex items-center justify-between'>
                <Label>Product Variants</Label>
                <button
                  type='button'
                  onClick={addVariant}
                  className='flex items-center gap-2 text-success-60 hover:text-success-70 text-sm font-medium'
                >
                  <PlusIcon className='h-4 w-4' />
                  Add New Variant
                </button>
              </div>

              {variants.map((variant, index) => (
                <div key={index} className='border rounded-lg p-4 space-y-3'>
                  <div className='flex items-center justify-between'>
                    <h4 className='text-sm font-medium text-gray-90'>
                      Variant {index + 1}
                    </h4>
                    {variants.length > 1 && (
                      <button
                        type='button'
                        onClick={() => removeVariant(index)}
                        className='text-destructive-50 hover:text-destructive-70'
                      >
                        <TrashIcon className='h-4 w-4' />
                      </button>
                    )}
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label>Variant Name</Label>
                      <Input
                        value={variant.name}
                        onChange={e =>
                          updateVariant(index, 'name', e.target.value)
                        }
                        placeholder='Enter variant name'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label>Unit Selling Price</Label>
                      <Input
                        type='number'
                        step='0.01'
                        value={variant.price}
                        onChange={e =>
                          updateVariant(index, 'price', Number(e.target.value))
                        }
                        placeholder='Enter selling price'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label>Quantity</Label>
                      <Input
                        type='number'
                        value={variant.quantity}
                        onChange={e =>
                          updateVariant(
                            index,
                            'quantity',
                            Number(e.target.value)
                          )
                        }
                        placeholder='Enter quantity'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label>Reorder Level</Label>
                      <Input
                        type='number'
                        value={variant.reorderLevel}
                        onChange={e =>
                          updateVariant(
                            index,
                            'reorderLevel',
                            Number(e.target.value)
                          )
                        }
                        placeholder='Enter reorder level'
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label>Unit Cost Price (Optional)</Label>
                      <Input
                        type='number'
                        step='0.01'
                        value={(variant as any).costPrice ?? ''}
                        onChange={e =>
                          updateVariant(
                            index,
                            'costPrice',
                            Number(e.target.value)
                          )
                        }
                        placeholder='Enter cost price'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label>Wholesale Price (Optional)</Label>
                      <Input
                        type='number'
                        step='0.01'
                        value={(variant as any).wholesalePrice ?? ''}
                        onChange={e =>
                          updateVariant(
                            index,
                            'wholesalePrice',
                            Number(e.target.value)
                          )
                        }
                        placeholder='Enter wholesale price'
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Advanced Settings (also for variant products) */}
              <div className='space-y-4 border rounded-lg p-4'>
                <h4 className='text-sm font-medium text-gray-90'>
                  Advanced Settings
                </h4>

                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='currency'
                    control={control}
                    render={({ field }) => (
                      <FilterSelect
                        label='Currency'
                        name='currency'
                        value={field.value || 'NGN'}
                        options={[
                          { value: 'NGN', label: 'NGN' },
                          { value: 'USD', label: 'USD' },
                          { value: 'EUR', label: 'EUR' },
                          { value: 'GBP', label: 'GBP' },
                        ]}
                        onChange={field.onChange}
                        className='w-full'
                      />
                    )}
                  />

                  <Controller
                    name='qualityGrade'
                    control={control}
                    render={({ field }) => (
                      <FilterSelect
                        label='Quality Grade'
                        name='qualityGrade'
                        value={field.value || 'A'}
                        options={[
                          { value: 'Premium', label: 'Premium' },
                          { value: 'A', label: 'Grade A' },
                          { value: 'B', label: 'Grade B' },
                          { value: 'C', label: 'Grade C' },
                          { value: 'Rejected', label: 'Rejected' },
                        ]}
                        onChange={field.onChange}
                        className='w-full'
                      />
                    )}
                  />
                </div>

                <Controller
                  name='storageWarehouse'
                  control={control}
                  render={({ field }) => (
                    <Input
                      label='Warehouse (Optional)'
                      {...field}
                      placeholder='e.g., Main Warehouse'
                    />
                  )}
                />

                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='autoReorderEnabled'
                    control={control}
                    render={({ field }) => (
                      <div className='flex items-center justify-between'>
                        <Label>Enable Auto Reorder</Label>
                        <input
                          type='checkbox'
                          checked={Boolean(field.value)}
                          onChange={e => field.onChange(e.target.checked)}
                        />
                      </div>
                    )}
                  />

                  <Controller
                    name='leadTimeDays'
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='Lead Time (days)'
                        type='number'
                        value={field.value ?? 7}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='autoReorderPoint'
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='Reorder Point (Optional)'
                        type='number'
                        value={field.value ?? ''}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                  <Controller
                    name='autoReorderQty'
                    control={control}
                    render={({ field }) => (
                      <Input
                        label='Reorder Quantity (Optional)'
                        type='number'
                        value={field.value ?? ''}
                        onChange={e => field.onChange(Number(e.target.value))}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Variant Summary */}
              {variantSummary && (
                <div className='border rounded-lg p-4 bg-gray-5'>
                  <div className='text-sm text-gray-80 font-medium mb-2'>
                    Variant Summary
                  </div>
                  <div className='grid grid-cols-3 gap-4 text-sm'>
                    <div>
                      <div className='text-gray-60'>Total Quantity</div>
                      <div className='font-medium'>
                        {variantSummary.totalQuantity}
                      </div>
                    </div>
                    <div>
                      <div className='text-gray-60'>
                        Top Selling Price (avg)
                      </div>
                      <div className='font-medium'>
                        {variantSummary.topPricing.sellingPrice}{' '}
                        {variantSummary.topPricing.currency}
                      </div>
                    </div>
                    <div>
                      <div className='text-gray-60'>
                        Top Cost Price (70% of avg)
                      </div>
                      <div className='font-medium'>
                        {variantSummary.topPricing.costPrice}{' '}
                        {variantSummary.topPricing.currency}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className='space-y-6'>
      {/* Step Indicator */}
      <StepIndicator
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepLabels={stepLabels}
      />

      {/* Step Content */}
      <div className='min-h-[400px]'>{renderStepContent()}</div>
    </div>
  );
};

export default InventoryForm;
